import { type PropsWithChildren, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import {
  Button,
  SectionContent,
  SectionGroup,
  SectionHeader,
  SectionItem,
  SectionItemValue,
  SectionTitle,
} from '@/components/ui'
import type { ResumoSimulacao } from '@/features/proposta/brasil/domain/ResumoProposta/resumo-simulacoes-proposta'
import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import { PropostaCompraContentSacocHPrice } from '@/features/proposta/brasil/pages/criar-proposta/resumo-proposta/components/proposta-compra-contents/content-sacoch-price'
import { PropostaCompraContentSacocVParcelado } from '@/features/proposta/brasil/pages/criar-proposta/resumo-proposta/components/proposta-compra-contents/content-sacocv-parcelado'
import { PropostaCompraContentSacocVSaldo } from '@/features/proposta/brasil/pages/criar-proposta/resumo-proposta/components/proposta-compra-contents/content-sacocv-saldo'
import { PropostaCompraContentVertical } from '@/features/proposta/brasil/pages/criar-proposta/resumo-proposta/components/proposta-compra-contents/content-vertical'
import type { TPropostaLocationState } from '@/features/proposta/brasil/types/proposta.type'
import { useFluxoPagamento } from '@/hooks/use-fluxo-pagamento'
import { useTypedLocation } from '@/hooks/use-typed-location'
import { FluxoPagamentoSheet } from '@/shared/simulacao/fluxo-pagamento'
import { FormaDePagamentoEnum, TipoAmortizacaoEnum } from '@/types/simulacao'
import { simulacaoKeys } from '@/utils/simulador-queries'

type TPropostaCompraSectionProps = {
  resumo: ResumoSimulacao
  vagaIds: TPropostaSchemaInput['informacoesIniciais']['vagaIds']
  plantaDescricao: string
  onEdit: (step: number) => void
}

type TPropostaCompraWrapperProps = PropsWithChildren<{
  onEdit: (step: number) => void
  resumo: ResumoSimulacao
}>

const STEP_INFORMACOES_INICIAIS = 0

const PropostaCompraWrapper = ({ resumo, children, onEdit }: TPropostaCompraWrapperProps) => {
  const { t } = useTranslation('proposta')
  const { state } = useTypedLocation<TPropostaLocationState>()
  const { data } = useFluxoPagamento(state.simulacoesIds, simulacaoKeys.fluxoDePagamento(state.simulacoesIds))

  return (
    <div>
      <SectionHeader>
        <SectionTitle>{t('resumo_proposta.proposta_compra.titulo')}</SectionTitle>
        <div className="hidden sm:flex sm:gap-2">
          <Render.If condition={data?.length}>
            {() => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={data ?? []}>
                <Button variant="ghost" className="h-8 text-sky-700 hover:bg-neutral-200 hover:text-sky-900">
                  <Icon name="eye-open" className="mr-1 size-4" />
                  {t('resumo_proposta.proposta_compra.fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
          <Button
            variant="ghost"
            className="h-8 text-sky-700 hover:bg-neutral-200 hover:text-sky-900"
            onClick={() => onEdit(STEP_INFORMACOES_INICIAIS)}
          >
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionHeader>
      <SectionContent className="sm:pb-0">
        <SectionGroup withBorder>
          <SectionItem label={t('resumo_proposta.proposta_compra.label.empreendimento')} colSpan={6} withRightBorder>
            <SectionItemValue>{resumo.nomeEmpreendimento}</SectionItemValue>
          </SectionItem>
          <SectionItem label={`${resumo.labelBloco} e ${resumo.labelProduto}`} colSpan={6}>
            <SectionItemValue>
              {resumo.blocoEmpreendimento} - {resumo.codigoProdutoPrincipal}
            </SectionItemValue>
          </SectionItem>
        </SectionGroup>

        {children}

        <div className="block sm:hidden">
          <div className="flex flex-col gap-3">
            <Render.If condition={data?.length}>
              {() => (
                <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={data ?? []}>
                  <Button variant="outline" className="w-full">
                    <Icon name="eye-open" className="mr-1 size-4" />
                    {t('resumo_proposta.proposta_compra.fluxo_pagamento')}
                  </Button>
                </FluxoPagamentoSheet>
              )}
            </Render.If>
            <Button variant="outline" className="w-full" onClick={() => onEdit(STEP_INFORMACOES_INICIAIS)}>
              <Icon name="pencil" className="mr-1 size-4" />
              {t('resumo_proposta.editar')}
            </Button>
          </div>
        </div>
      </SectionContent>
    </div>
  )
}

export function PropostaCompraSection({
  resumo,
  vagaIds,
  plantaDescricao,
  onEdit,
}: Readonly<TPropostaCompraSectionProps>) {
  const contentComponent = useMemo(() => {
    if (resumo.tipoDeAmortizacao === TipoAmortizacaoEnum.SacocV) {
      if (resumo.formaDePagamento === FormaDePagamentoEnum.Parcelado) {
        return <PropostaCompraContentSacocVParcelado resumo={resumo} />
      }
      return <PropostaCompraContentSacocVSaldo resumo={resumo} />
    }

    if (
      resumo.tipoDeAmortizacao === TipoAmortizacaoEnum.SacocH ||
      resumo.tipoDeAmortizacao === TipoAmortizacaoEnum.Price
    ) {
      return <PropostaCompraContentSacocHPrice resumo={resumo} />
    }

    return <PropostaCompraContentVertical resumo={resumo} vagaIds={vagaIds} plantaDescricao={plantaDescricao} />
  }, [resumo, vagaIds, plantaDescricao])

  return (
    <PropostaCompraWrapper resumo={resumo} onEdit={onEdit}>
      {contentComponent}
    </PropostaCompraWrapper>
  )
}
